import {
  ActionBar,
  Button,
  Checkbox,
  ClassesIllustration,
  Dropdown,
  DropdownMenu,
  EmptyState,
  ImportIcon,
  LazyLoader,
} from '@bp/ui-components';
import classNames from 'classnames';
import { getFragmentData, graphql } from 'client/gql';
import {
  ForeignSyncMetaCreateInput,
  ForeignSyncMetasFieldsFragment,
  ForeignSyncMetaUpdateInput,
} from 'client/gql/graphql';
import { BpCard } from 'components/BpCard/BpCard';
import { Form, Formik } from 'formik';
import { SettingsImportPlanungSubpageOutletContext } from 'pages/Settings/subpages/SettingsImportPlanungSubpage';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useMutation } from 'urql';
import { backendApi } from 'utils/backendApi';
import { createForeignRefId, SyncMaster } from 'utils/create-foreignRefId';
import { naturalStringSort } from 'utils/stringHelper';
import { showSuccessToast, showUserDbErrorToast } from 'utils/toasts';
import {
  CreateForeignSyncMetaDocument,
  ForeignSyncMetasFieldsDocument,
  UpdateForeignSyncMetaDocument,
} from '../queries/queries';
import { ColorType } from '../types';
import styles from './_PlanungImportForms.module.scss';
import { PlanungImportClassesFormRow } from './PlanungImportClassesFormRow';

const PlanungImportClassesFormFragment = graphql(`
  fragment PlanungImportClassesFormFields on Query {
    classes(
      options: { sort: [{ name: ASC }] }
      where: { schoolYear: { uuid: $schoolYearUuid }, organization: { uuid: $organizationUuid } }
    ) {
      __typename
      uuid
      name
    }
  }
`);

type PlanungImportClassesFormType = {
  classes: ClassModel[];
};

type ClassModel = {
  uuid: string;
  selected: boolean;
  name: string;
  grade: string;
  gradeGroup: string;
  color: ColorType;
  connectClassUuid: string | null;
  connected: boolean;
  ignore: boolean;
};

export const PlanungImportClassesForm = () => {
  const { t } = useTranslation();

  const { bpData, planungData, refetch, planungSchoolyear, bpSchoolyear } =
    useOutletContext<SettingsImportPlanungSubpageOutletContext>();

  const metaFragment = getFragmentData(ForeignSyncMetasFieldsDocument, bpData);
  const formFragment = getFragmentData(PlanungImportClassesFormFragment, bpData);

  const foreignSyncMetas: Pick<ForeignSyncMetasFieldsFragment, 'foreignSyncMetas'>['foreignSyncMetas'] | undefined =
    useMemo(
      () => metaFragment?.foreignSyncMetas.filter((fsm) => !fsm.syncTarget || fsm.syncTarget?.__typename === 'Class'),
      [metaFragment],
    );

  const classes = formFragment?.classes ?? [];
  const planungClasses = planungData?.classes ?? [];

  const [, createForeignSyncMeta] = useMutation(CreateForeignSyncMetaDocument);
  const [, updateForeignSyncMeta] = useMutation(UpdateForeignSyncMetaDocument);

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState<string | null>(null);
  const [classModel, setClassModel] = useState<ClassModel[]>([]);

  const handleImport = async () => {
    const response = await backendApi.post(
      `/planung/import/entities/classes`,
      JSON.stringify({
        planungSchoolyear,
        bpSchoolyear,
      }),
    );
    return JSON.stringify(await response.json());
  };

  const handleSubmit = async () => {
    setLoading(true);

    const selectedClasses = classModel.filter((c) => c.selected);

    const ignore = false; // this 'ignore' idea is not really well implemented. so we default it to false.
    const createInput = (importClass: ClassModel, foreignRefId: string): ForeignSyncMetaCreateInput => ({
      foreignRefId: foreignRefId,
      syncSource: SyncMaster.planung,
      ignore,
      syncTarget: { Class: { connect: { where: { node: { uuid: importClass.connectClassUuid } } } } },
    });

    const updateInput = (importClass: ClassModel): ForeignSyncMetaUpdateInput => ({
      ignore,
      syncTarget: importClass.connectClassUuid
        ? { Class: { disconnect: {}, connect: { where: { node: { uuid: importClass.connectClassUuid } } } } }
        : { Class: { disconnect: {} } },
    });

    let errors: number = 0;

    for (const importClass of selectedClasses) {
      const foreignRefId = createForeignRefId(SyncMaster.planung, importClass.uuid);

      const hasForeignSyncMeta = foreignSyncMetas?.find((c) => c.foreignRefId === foreignRefId);
      if (hasForeignSyncMeta) {
        const { error } = await updateForeignSyncMeta({
          update: updateInput(importClass),
          where: { foreignRefId: foreignRefId },
        });
        error && errors++;
      } else {
        const { error } = await createForeignSyncMeta({ input: createInput(importClass, foreignRefId) });
        error && errors++;
      }
    }

    if (errors > 0) {
      showUserDbErrorToast();
    } else {
      await handleImport();
      showSuccessToast(t('planungImport.success'));
    }

    refetch();
    setLoading(false);
  };

  const initialValues: PlanungImportClassesFormType = {
    // handle via state
    classes: [],
  };

  function handleSelect(uuid: string, select: boolean) {
    const newModel = classModel.map((pc) => ({
      ...pc,
      selected: pc.uuid === uuid ? select : pc.selected,
      connectClassUuid: pc.uuid === uuid && !pc.connectClassUuid && !select ? null : pc.connectClassUuid,
    }));
    setClassModel(newModel);
  }

  function handleClassSelect(uuid: string, connectClassUuid: string | null) {
    const newModel = classModel.map((pc) => ({
      ...pc,
      connectClassUuid: pc.uuid === uuid ? connectClassUuid : pc.connectClassUuid,
    }));
    setClassModel(newModel);
  }

  function handleSelectAll(select: boolean) {
    const newModel = classModel.map((pc) => ({
      ...pc,
      selected: filteredModel.includes(pc) ? select : pc.selected,
      connectClassUuid: !pc.connectClassUuid && !select ? null : pc.connectClassUuid,
    }));
    setClassModel(newModel);
  }

  function handleSelectImported(select: boolean) {
    const newModel = classModel.map((pc) => ({
      ...pc,
      selected: filteredModel.includes(pc) && pc.connected ? select : pc.selected,
      connectClassUuid: !pc.connectClassUuid && !select ? null : pc.connectClassUuid,
    }));
    setClassModel(newModel);
  }

  function handleSelectNotImported(select: boolean) {
    const newModel = classModel.map((pc) => ({
      ...pc,
      selected: filteredModel.includes(pc) && !pc.connected ? select : pc.selected,
      connectClassUuid: !pc.connectClassUuid && !select ? null : pc.connectClassUuid,
    }));
    setClassModel(newModel);
  }

  const filteredModel = classModel
    .filter((c) => (!search ? true : c.name.toLowerCase().includes(search?.toLowerCase())))
    .sort((a, b) => {
      // imported
      if (a.connected !== b.connected) {
        return a.connected ? 1 : -1;
      }
      // sort by grade, then gradeGroup, then name
      if (a.grade !== b.grade) {
        if (a.grade === '') return 1;
        if (b.grade === '') return -1;
        return naturalStringSort(a.grade, b.grade);
      }
      if (a.gradeGroup !== b.gradeGroup) {
        return naturalStringSort(a.gradeGroup, b.gradeGroup);
      }
      return naturalStringSort(a.name, b.name);
    });

  const availableClasses: { uuid: string; name: string }[] = useMemo(() => {
    const connectedClassUuids = classModel.map((pc) => pc.connectClassUuid).filter((uuid): uuid is string => !!uuid);
    return classes
      .filter((c) => c && !connectedClassUuids.includes(c.uuid))
      .map((c) => ({ uuid: c.uuid, name: c.name ?? '' }));
  }, [classModel, classes]);

  useEffect(() => {
    const model: ClassModel[] = planungClasses
      .sort((a, b) => naturalStringSort(a.name, b.name))
      .map((pc) => {
        const fsm = foreignSyncMetas?.find(
          (fsm) => fsm.foreignRefId === createForeignRefId(SyncMaster.planung, pc.uuid),
        );
        const connected = !!fsm && !!fsm.syncTarget && fsm.syncTarget.__typename === 'Class';
        const preSelectClass = classes.find((c) => c.name === pc.name);
        return {
          uuid: pc.uuid,
          selected: !connected,
          name: pc.name ?? '',
          grade: pc.grade?.toString() ?? '',
          gradeGroup: pc.gradeGroupName ?? '',
          color: pc.colorType,
          connectClassUuid: connected ? (fsm.syncTarget?.uuid ?? null) : (preSelectClass?.uuid ?? null),
          connected: connected,
          ignore: !!fsm?.ignore && fsm.ignore,
        };
      });
    setClassModel(model);
  }, [planungClasses, classes, foreignSyncMetas]);

  return (
    <Formik<PlanungImportClassesFormType> initialValues={initialValues} onSubmit={() => handleSubmit()}>
      {() => (
        <Form className={styles['planung-import-form']}>
          {loading ? (
            <LazyLoader size='xxl' borderRadius='m' forceHeight='25vh' />
          ) : (
            <BpCard noPadding header={{ headline: t('planungImport.importClasses') }}>
              <ActionBar
                isOnWhite
                onGlobalFilterChange={(value) => setSearch(value)}
                className={styles['action-bar']}
                extendedActionsLeft={
                  <div className={styles.overview}>
                    {t('planungImport.importOverview', {
                      sum: classModel.filter((c) => c.selected).length,
                      total: filteredModel.length,
                    })}
                  </div>
                }
                extendedActionsRight={
                  <>
                    <Dropdown trigger={<Button hierarchy='tertiary'>{t('common.selection')}</Button>}>
                      <DropdownMenu
                        data={[
                          { label: t('planungImport.selectNotImported'), onClick: () => handleSelectNotImported(true) },
                          {
                            label: t('planungImport.deselectNotImported'),
                            onClick: () => handleSelectNotImported(false),
                          },
                          { type: 'ruler' },
                          { label: t('planungImport.selectImported'), onClick: () => handleSelectImported(true) },
                          { label: t('planungImport.deselectImported'), onClick: () => handleSelectImported(false) },
                          { type: 'ruler' },
                          { label: t('common.selectAll'), onClick: () => handleSelectAll(true) },
                          { label: t('planungImport.deselectAll'), onClick: () => handleSelectAll(false) },
                        ]}
                      />
                    </Dropdown>
                    <Button
                      type='submit'
                      icon={<ImportIcon />}
                      disabled={classModel.filter((c) => c.selected).length === 0}
                    >
                      {t('planungImport.createClasses')}
                    </Button>
                  </>
                }
              />

              <div className={classNames(styles.form, styles.classes)}>
                {filteredModel.length === 0 ? (
                  <EmptyState icon={<ClassesIllustration />} title={t('classes.empty.title')} forcedHeight='30vh' />
                ) : (
                  <>
                    <div className={styles.header}>
                      <div className={styles.checkbox}>
                        <Checkbox
                          onChange={(event) => {
                            handleSelectAll(event.target.checked);
                          }}
                          checked={
                            filteredModel.length > 0 &&
                            filteredModel.filter((t) => t.selected).length === filteredModel.length
                          }
                          name={`toggle-classes-all`}
                        />
                        <div>{t('planungImport.classInPlanung')}</div>
                      </div>
                      <div>{t('classes.grade')}</div>
                      <div>{t('classes.gradeGroup')}</div>
                      <div>{t('common.color')}</div>
                      <div></div>
                      <div>{t('planungImport.classInBp', { count: 1 })}</div>
                      <div>{t('planungImport.connected')}</div>
                    </div>
                    {filteredModel.map((pc) => (
                      <PlanungImportClassesFormRow
                        key={pc.uuid}
                        uuid={pc.uuid}
                        selected={pc.selected}
                        name={pc.name}
                        grade={pc.grade}
                        gradeGroup={pc.gradeGroup}
                        color={pc.color}
                        classes={availableClasses}
                        selectedClass={
                          pc.connectClassUuid && classes.find((c) => c.uuid === pc.connectClassUuid)
                            ? {
                                value: classes.find((c) => c.uuid === pc.connectClassUuid)?.uuid ?? '',
                                label: classes.find((c) => c.uuid === pc.connectClassUuid)?.name ?? '',
                              }
                            : null
                        }
                        connected={pc.connected}
                        onSelect={(isSelected) => handleSelect(pc.uuid, isSelected)}
                        onClassSelect={(classUuid) => handleClassSelect(pc.uuid, classUuid)}
                      />
                    ))}
                  </>
                )}
              </div>
            </BpCard>
          )}
        </Form>
      )}
    </Formik>
  );
};
